import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import ActionButtons from "../../../../../../../components/Generic/ActionButtons";
import SkeletonDetails from "../../../../../../../components/Generic/LoadingSkeleton";
import { VoyageDetails } from "Components/Tracking";
import { ContainerPosition, ContainerStatus } from "../../../../../../../models/marine.traffic";
import MarineTrafficService from "../../../../../../../services/marine.traffic.service";
import { ShipmentStatus, ShipmentType } from "../../../../../../../util/enums";
import { useShipmentContext } from "../../hooks/useContext";
import { TabBase } from "../../types";
import useStyles from "./styles";

interface WaybillTrackingMapProps extends TabBase {}

export default function WaybillTrackingMap(props: WaybillTrackingMapProps) {
  const { shipment: waybill } = useShipmentContext();
  const classes = useStyles();
  const { actionButtonProps } = props;

  const [containerStatus, setContainerStatus] = useState<ContainerStatus>();
  const [containerPosition, setContainerPosition] = useState<ContainerPosition>();

  const shipmentId = useMemo(() => {
    if (!waybill) return undefined;

    if (waybill.shipmentType === ShipmentType.SEA) {
      const seaFields = [waybill.billOfLanding, waybill.bookingNo, waybill.containerNumbers];
      return seaFields.find((field) => !!field);
    }

    if (waybill.shipmentType === ShipmentType.AIR) {
      return waybill.airwayBill;
    }

    return undefined;
  }, [waybill]);

  const { isFetching } = useQuery(
    ["container.status", waybill?.id, shipmentId],
    () =>
      MarineTrafficService.viewShipmentTracking(shipmentId ?? "", waybill?.id?.toString() ?? ""),
    {
      enabled:
        !!waybill?.marineTrafficId &&
        waybill?.shipmentTracking?.shipmentStatus?.toLowerCase() !==
          ShipmentStatus.WAITING_FOR_UPDATE.toLowerCase(),
      refetchOnWindowFocus: false,
      cacheTime: 0,
      retry: false,
      onSuccess: (response) => {
        if (!response?.data) {
          setContainerStatus(undefined);
          setContainerPosition(undefined);
          return;
        }
        setContainerStatus(response.data);
        const position = {
          TIMESTAMP: waybill?.shipmentTracking?.vesselPosition?.updatedAt,
          LAT: waybill?.shipmentTracking?.vesselPosition?.latitude ?? "",
          LON: waybill?.shipmentTracking?.vesselPosition?.longitude ?? "",
        } as unknown as ContainerPosition;

        setContainerPosition(position);
      },
    }
  );

  return (
    <React.Fragment>
      {isFetching ? (
        <div style={{ padding: "24px 32px" }}>
          <SkeletonDetails />
        </div>
      ) : (
        <div
          style={{
            padding: "24px",
          }}
        >
          <VoyageDetails
            shipment={waybill}
            containerPosition={containerPosition}
            containerStatus={containerStatus}
            viewLivePosition={process.env.NODE_DATABASE !== "work"}
          />
          <div className={classes.pt32}>
            <ActionButtons {...actionButtonProps} />
          </div>
        </div>
      )}
    </React.Fragment>
  );
}
